import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

//MUI
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getCurrentYear } from '../../../../../utils/getCurrentYear';

//BIKENTA
import BiomasSection from '../BiomasSection';
import PlanningMassDescription from './PlanningMassDescription';
import PlanningInterventionSection from './PlanningInterventionSection';
import AbsorptionPlanSeccion from '../AbsorptionPlanSection';
import EditableData from '../EditableData';

//UTILS
import {
  BASELINE_CO2,
  COMMERCIAL_PURPOSE_OPTIONS,
  FORESTRY_PROJECT,
  SPECIES_INCLUDED_BIOMASCO2
} from '../../../../../config';
import round from '../../../../../utils/round';
import {useTranslation} from 'react-i18next';

//STYLES
const containerSx = {
  display: 'flex',
  flexDirection: 'column',
  margin: '10px',
  width: '98%'
};


const paramsToApi = (commonParams) => ({
  basimetric_area: commonParams.basimetric_area,
  dominant_height: commonParams.dominant_height,
  initial_year: commonParams.initial_year,
  machinery_difficulty: commonParams.machinery_difficulty,
  number_per_ha: commonParams.number_per_ha,
  intervention_zone_id: commonParams.intervention_zone_id,
  site_index: commonParams.site_index,
  species_id: commonParams.species_id,
  time_frame: commonParams.final_year - commonParams.initial_year,
  fire_risk: commonParams.fire_risk,
  year_data: commonParams.year_data,
  initial_age: commonParams.initial_age
});


const PlanningBiomassForm = ({
  selectedInterventionZone,
  exanteProject,
  isOffline,
  onBiomasCO2Close,
  onBiomasGetSummaries,
  onCreateExanteProject,
  onInterventionZoneUpdate
}) => {

  const {t} = useTranslation();
  const {id, area, species, machinery_difficulty, fire_risk} = selectedInterventionZone;

  const absorptionBaseLine = useMemo(() => exanteProject && exanteProject.filter(project => project.type === BASELINE_CO2).length
    ? exanteProject.find(project => project.type === BASELINE_CO2)
    : {}, [exanteProject]);
  const absorptionProject = useMemo(() => exanteProject && exanteProject.filter(project => project.type === FORESTRY_PROJECT).length
    ? exanteProject.find(project => project.type === FORESTRY_PROJECT)
    : {}, [exanteProject]);

  const minYear = useMemo(() => exanteProject.length
    ? exanteProject.at(-1).initial_year
    : getCurrentYear(), [exanteProject]);
  const maxYear = useMemo(() => exanteProject.length
    ? exanteProject.at(-1).initial_year + exanteProject.at(-1).time_frame
    : getCurrentYear(), [exanteProject]);

  const getXorforId = (bbddId) => SPECIES_INCLUDED_BIOMASCO2.find(species => species.bbddId === bbddId)?.xorforId;

  const initialCommonParams = useMemo(() => ({
    intervention_zone_id: selectedInterventionZone.id,
    year_data: absorptionBaseLine.year_data || new Date().getFullYear() - 1,
    species_id: absorptionBaseLine?.species_id || getXorforId(species[0].id),
    area: area,
    number_per_ha: absorptionBaseLine?.number_per_ha || Math.trunc(species[0].existences.treeFeet),
    dominant_height: absorptionBaseLine?.dominant_height || Math.trunc(species[0].existences.height),
    basimetric_area: absorptionBaseLine?.basimetric_area || species[0].existences.basometricArea,
    site_index: 0,
    initial_year: absorptionBaseLine?.initial_year || new Date().getFullYear(),
    final_year: (absorptionBaseLine?.initial_year + absorptionBaseLine?.time_frame) || new Date().getFullYear() + 1,
    initial_age: absorptionBaseLine?.initial_age || species[0].age,
  }), [absorptionBaseLine]);

  const initialBaseLineParams = useMemo(() => ({
    type: BASELINE_CO2,
    has_weeding: absorptionBaseLine?.has_weeding || false,
    has_pruning: absorptionBaseLine?.has_pruning || false,
    commercial_purpose: absorptionBaseLine?.commercial_purpose || COMMERCIAL_PURPOSE_OPTIONS[1].id,
    planting: absorptionBaseLine?.planting || undefined,
    intermediate_cuttings: absorptionBaseLine?.intermediate_cuttings || [],
    final_cutting: absorptionBaseLine?.final_cutting || undefined
  }), [absorptionBaseLine]);

  const initialProjectParams = useMemo(() => ({
    type: FORESTRY_PROJECT,
    has_weeding: absorptionProject?.has_weeding || false,
    has_pruning: absorptionProject?.has_pruning ||false,
    commercial_purpose: absorptionProject?.commercial_purpose || COMMERCIAL_PURPOSE_OPTIONS[0].id,
    prediction: absorptionProject?.prediction,
    planting: absorptionProject?.planting,
    intermediate_cuttings: absorptionProject?.intermediate_cuttings || [],
    final_cutting: absorptionProject?.final_cutting
  }), [absorptionProject]);

  const [commonParams, setCommonParams] = useState(undefined);
  const [baseLine, setBaseLine] = useState(undefined);
  const [project, setProject] = useState(undefined);

  useEffect(() => setCommonParams(initialCommonParams), [initialCommonParams]);
  useEffect(() => setBaseLine(initialBaseLineParams), [initialBaseLineParams]);
  useEffect(() => setProject(initialProjectParams), [initialProjectParams]);

  const handleCalculateBaseLine = () => onCreateExanteProject({...paramsToApi(commonParams), ...baseLine});

  const handleCalculateProject = () => onCreateExanteProject({...paramsToApi(commonParams), ...project});

  useMemo(() => onBiomasGetSummaries(), [exanteProject]);

  const handleBiomasCO2Close = () => {
    setBaseLine(initialBaseLineParams);
    setProject(initialProjectParams);
    onBiomasCO2Close();
  };

  const handleInterventionZoneChange = (value) => {
    setCommonParams({...commonParams, ...value});
    onInterventionZoneUpdate(value);
  };

  if (!commonParams) {
    return <></>;
  }

  //ERRORS
  const isValid = (val) => val !== undefined && val !== 0;

  const {
    year_data,
    initial_year,
    final_year,
    number_per_ha,
    dominant_height,
    basimetric_area
  } = commonParams;

  const hasRequiredValues =
  isValid(year_data) &&
  isValid(initial_year) &&
  isValid(final_year) &&
  isValid(number_per_ha) &&
  isValid(dominant_height) &&
  isValid(basimetric_area) &&
  fire_risk !== null &&
  machinery_difficulty !== null;
  
  return <Box sx={containerSx}>
    {/*DESCRIPCIÓN DE LA MASA----------------------------------------------------------------------------------------*/}
    <BiomasSection number={1} label={t('Descripción de la masa')}
      aditionalProperty={
        <EditableData
          onChange={(value) => setCommonParams({...commonParams, year_data: value})}
          label={t('Año de captura de datos')}
          isRequired={true}
          value={commonParams.year_data}
          sx={{ml: 4, mb: 1}}
          max={commonParams.final_year}
        />
      }>
      <>
        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 4}}>
          <PlanningMassDescription
            intervention_zone_id={id}
            species_id={commonParams.species_id.toString()}
            age={commonParams.initial_age}
            area={round(commonParams.area)}
            number_per_ha={Math.trunc(commonParams.number_per_ha)}
            dominant_height={commonParams.dominant_height}
            basimetric_area={commonParams.basimetric_area}
            site_index={commonParams.site_index}
            fire_risk={fire_risk}
            machinery_difficulty={machinery_difficulty}
            onMassChange={(value) => setCommonParams({...commonParams, ...value})}
            onInterventionZoneUpdate={handleInterventionZoneChange}
          />
        </Box>
      </>
    </BiomasSection>

    {/*PROYECTO DE ABSORCIÓN-----------------------------------------------------------------------------------------*/}
    <BiomasSection number={2} label={t('Proyecto de absorción')} sx={{display: 'flex', flexDirection: 'column', mt: 2}}
      aditionalProperty={
        <Box sx={{display: 'flex', flexDirection: 'row', ml: 4, mb: 1, gap: 2}}>
          <EditableData
            label={t('Año de inicio')}
            onChange={(value) => setCommonParams({...commonParams, initial_year: value})}
            value={commonParams.initial_year}
            max={commonParams.final_year-1}
            isRequired={true}/>
          <EditableData
            label={t('Año de fin')}
            onChange={(value) => setCommonParams({...commonParams, final_year: value})}
            value={commonParams.final_year}
            min={commonParams.initial_year+1}
            isRequired={true}/>
        </Box>
      }
    >
      <Box>
        <Typography variant='body2' sx={{fontWeight: 900, bgcolor: '#ededed', py: 0.5, px: 1, mb: 1}}>A. {t('Línea base').toUpperCase()}</Typography>
        <PlanningInterventionSection
          absorptionBaseLine={absorptionBaseLine}
          absorptionProject={absorptionProject}
          planningInterventions={baseLine}
          hasRequiredValues={hasRequiredValues}
          isOffline={isOffline}
          mode={BASELINE_CO2}
          onPlanningInterventionChange={setBaseLine}
          onCreateExanteProject={handleCalculateBaseLine}
        />
        <Typography variant='body2' sx={{fontWeight: 900, bgcolor: '#ededed', py: 0.5, px: 1, mb: 1}}>B. {t('Proyecto Selvícola').toUpperCase()}</Typography>
        <PlanningInterventionSection
          absorptionBaseLine={absorptionBaseLine}
          absorptionProject={absorptionProject}
          planningInterventions={project}
          hasRequiredValues={hasRequiredValues && absorptionBaseLine !== null}
          isOffline={isOffline}
          mode={FORESTRY_PROJECT}
          onPlanningInterventionChange={setProject}
          onCreateExanteProject={handleCalculateProject}
        />
        <Box>
          <Typography variant='body2' sx={{fontWeight: 900, bgcolor: '#ededed', py: 0.5, px: 1, mb: 1}}>C. {t('Plan de absorción').toUpperCase()}</Typography>
          <AbsorptionPlanSeccion
            minChartYear={minYear}
            maxChartYear={maxYear}
            firstCalculation={absorptionBaseLine}
            finalCalculation={absorptionProject}
            isMonitoring={false}
          />
        </Box>
      </Box>
    </BiomasSection>
    <Button variant='contained' color='secondary' sx={{ml: 4, my: 2, width: '250px'}} onClick={handleBiomasCO2Close}>{t('salir')}</Button>
  </Box>;
};

export default PlanningBiomassForm;

PlanningBiomassForm.propTypes = {
  isOffline: PropTypes.bool,
  exanteProject: PropTypes.array,
  selectedInterventionZone: PropTypes.object.isRequired,
  onBiomasCO2Close: PropTypes.func.isRequired,
  onBiomasGetSummaries: PropTypes.func.isRequired,
  onCreateExanteProject: PropTypes.func.isRequired,
  onInterventionZoneUpdate: PropTypes.func.isRequired
};